/**
 * @fileoverview Handler for document screenshot icon click events.
 *
 * This module provides the logic for handling clicks on document upload
 * screenshot icons, preparing the image data and opening the screenshot
 * viewer modal.
 *
 * @module handleDocumentScreenshotClick
 */

import { Dispatch } from "@reduxjs/toolkit";
import { ImageViewerWithTextAndBadgeProps } from "../components/imageViewerWithTextAndBadge/imageViewerWithTextAndBadge";
import {
  DocumentUploadScreenshot,
  JCodeDetails,
} from "../data-model/medicalPaOrdersModel";
import { ModalId } from "../enums/modalId";
import { setOpenedModalId } from "../redux/slice/modalSlice";
import {
  getBatchBgColorByUploadStatus,
  getBatchTextColorByUploadStatus,
  getPriorityByUploadStatus,
  getScreenshotPathByDocPath,
  getUpdatedAtByDocPath,
  sortImageBasedOnDocStatusInDescendingOrder,
} from "./documentScreenshotUtils";

/**
 * Parameters required for handling document screenshot icon clicks.
 */
export interface HandleDocumentScreenshotClickParams {
  /** The J-code details containing document information */
  rowData: JCodeDetails;
  /** Array of document upload screenshots from the order */
  screenshots: DocumentUploadScreenshot[] | undefined;
  /** Redux dispatch function */
  dispatch: Dispatch;
}

/**
 * Handles the click event on a document screenshot icon.
 *
 * @description
 * This function processes the documents from a J-code, extracts their
 * screenshot information, applies status-based styling, sorts them by
 * priority (errors first), and opens the document screenshot viewer modal.
 *
 * The images are sorted in the following priority order:
 * 1. ERROR - Shown first (highest priority)
 * 2. IN_PROGRESS - Shown second
 * 3. SUCCESS - Shown third
 * 4. Other/Unknown - Shown last
 *
 * @param {HandleDocumentScreenshotClickParams} params - The parameters object
 * @param {JCodeDetails} params.rowData - The J-code details containing documents
 * @param {DocumentUploadScreenshot[]} params.screenshots - Document upload screenshots
 * @param {Dispatch} params.dispatch - Redux dispatch function
 *
 * @example
 * handleDocumentScreenshotClick({
 *   rowData: jCodeDetails,
 *   screenshots: singleMedicalPaOrder?.auth_on_file?.screenshots?.document_upload_screenshots,
 *   dispatch: dispatch,
 * });
 */
export const handleDocumentScreenshotClick = ({
  rowData,
  screenshots,
  dispatch,
}: HandleDocumentScreenshotClickParams): void => {
  const jCodeDocuments = rowData?.documents ?? [];

  const imagesWithBadges: ImageViewerWithTextAndBadgeProps[] =
    jCodeDocuments.map((doc, index) => ({
      imagePath: getScreenshotPathByDocPath(screenshots, doc?.doc_path ?? ""),
      badgeDetails: {
        batchText: doc?.upload_status ?? "No Status",
        batchBgColor: getBatchBgColorByUploadStatus(doc?.upload_status ?? ""),
        batchTextColor: getBatchTextColorByUploadStatus(
          doc?.upload_status ?? "",
        ),
        batchId: doc?.id ?? "",
      },
      shouldShowBottomBorder: index !== jCodeDocuments.length - 1,
      text: doc?.doc_title ?? "No Title",
      updatedAt: getUpdatedAtByDocPath(screenshots, doc?.doc_path ?? ""),
      priroity: getPriorityByUploadStatus(doc?.upload_status ?? ""),
    }));

  const sortedImagesWithBadges =
    sortImageBasedOnDocStatusInDescendingOrder(imagesWithBadges);

  dispatch(
    setOpenedModalId({
      id: ModalId.DOCUMENT_SCREENSHOT_VIEWER_MODAL,
      metaData: {
        title: "Document Screenshots",
        imagesWithBadges: sortedImagesWithBadges,
      },
    }),
  );
};
